import { useEffect, useMemo, useState } from "react";

import { keyBy, orderBy } from "lodash";

import { GenericTooltip } from "@components/tooltip-container";
import { Checkbox } from "@shared/checkbox";

import { type MetricConfig } from "../../metric-category-builder";
import { Accordion } from "../accordion";

import { FbAttributionWindowsSelector } from "./fb-attribution-windows-selector";

export const FB_WINDOWS = [
  { value: "1DClick", name: "1D Click" },
  { value: "7DClick", name: "7D Click" },
  { value: "1DView", name: "1D View" },
  { value: "7DClick1DView", name: "7D Click, 1D View" },
] as const;

const FB_WINDOWS_NAMES = keyBy(FB_WINDOWS, "value");

export type FB_WINDOW = (typeof FB_WINDOWS)[number];

interface FbMetricsSelectorProps<T extends MetricConfig> {
  metricOrderedVisibility: T[];
  toggleMetricVisibility: (arg0: T, visible?: boolean) => void;
}

export const FbMetricsSelector = <T extends MetricConfig>({
  metricOrderedVisibility,
  toggleMetricVisibility,
}: FbMetricsSelectorProps<T>) => {
  const visibleFbMetrics = metricOrderedVisibility.filter(
    ({ visible }) => visible,
  );

  const [selectedFbWindows, setSelectedFbWindows] = useState<
    Set<FB_WINDOW["value"]>
  >(
    new Set(
      FB_WINDOWS.reduce<Set<FB_WINDOW["value"]>>((acc, { value }) => {
        if (value === "1DView") {
          // This check is because 1DView is its own metric and also inside 7DClick1DView
          const all1DViews = visibleFbMetrics.filter(({ id }) =>
            id.endsWith(value),
          );
          const exactly1DViews = all1DViews.filter(
            ({ id }) => !id.includes("7DClick"),
          );
          if (exactly1DViews.length > 0) {
            acc.add(value);
          }
        } else if (visibleFbMetrics.some(({ id }) => id.endsWith(value))) {
          acc.add(value);
        }
        return acc;
      }, new Set()),
    ),
  );

  useEffect(() => {
    setSelectedFbWindows((prevSelectedFbWindows) => {
      if (prevSelectedFbWindows.size === 0) {
        return new Set(["1DClick"]);
      }
      return prevSelectedFbWindows;
    });
  }, [setSelectedFbWindows]);

  const nonModalMetricsSet = useMemo(() => {
    return new Set(
      metricOrderedVisibility
        .filter((metric) => metric.isNonModal)
        .map((m) => m.id),
    );
  }, [metricOrderedVisibility]);

  const [selectedFbMetrics, setSelectedFbMetrics] = useState<Set<string>>(
    new Set(visibleFbMetrics.map(({ id }) => cleanFbMetricId(id))),
  );

  const fbMetrics = metricsWithoutAttributionWindow(metricOrderedVisibility);
  const fbReportedMetrics = fbMetrics.filter(
    ({ name }) => !name.toLowerCase().includes("shop"),
  );
  const fbShopMetrics = fbMetrics.filter(({ name }) =>
    name.toLowerCase().includes("shop"),
  );

  const toggleFbMetricVisibility = (metric: T) => {
    setSelectedFbMetrics((prevSelectedFbMetrics) => {
      const newSelectedFbMetrics = new Set(prevSelectedFbMetrics);
      if (newSelectedFbMetrics.has(metric.id)) {
        newSelectedFbMetrics.delete(metric.id);
      } else {
        newSelectedFbMetrics.add(metric.id);
      }
      return newSelectedFbMetrics;
    });
  };

  useEffect(() => {
    const visibleFbMetricsIds: Set<string> = new Set([]);
    selectedFbWindows.forEach((window) => {
      selectedFbMetrics.forEach((metric) => {
        const metricId = `${metric}${window}`;

        visibleFbMetricsIds.add(metricId);
      });
    });

    selectedFbMetrics.forEach((metric) => {
      if (nonModalMetricsSet.has(metric)) {
        visibleFbMetricsIds.add(metric);
      }
    });

    metricOrderedVisibility.forEach((metric) => {
      if (visibleFbMetricsIds.has(metric.id) && !metric.visible) {
        toggleMetricVisibility(metric);
      } else if (!visibleFbMetricsIds.has(metric.id) && metric.visible) {
        toggleMetricVisibility(metric);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFbWindows, selectedFbMetrics]);

  return (
    <div
      className="p-5 overflow-y-scroll"
      data-testid="metric-visibility-panel"
    >
      <FbAttributionWindowsSelector
        selectedFbWindows={selectedFbWindows}
        setSelectedFbWindows={setSelectedFbWindows}
      />
      <MetricsSelector
        title="Select Facebook reported metrics"
        fbMetrics={fbReportedMetrics}
        selectedFbMetrics={selectedFbMetrics}
        setSelectedFbMetrics={setSelectedFbMetrics}
        toggleFbMetricVisibility={toggleFbMetricVisibility}
        metricOrderedVisibility={metricOrderedVisibility}
        selectedFbWindows={selectedFbWindows}
        testId="fb-reported-metrics"
      />
      <MetricsSelector
        title="Select Meta Shop reported metrics"
        fbMetrics={fbShopMetrics}
        selectedFbMetrics={selectedFbMetrics}
        setSelectedFbMetrics={setSelectedFbMetrics}
        toggleFbMetricVisibility={toggleFbMetricVisibility}
        metricOrderedVisibility={metricOrderedVisibility}
        selectedFbWindows={selectedFbWindows}
        testId="fb-shop-metrics"
      />
    </div>
  );
};

const cleanFbMetricId = (metricId: string) => {
  const fbWindowsRegex = new RegExp(
    FB_WINDOWS.map(({ value }) => value)
      .reverse()
      .join("|"),
  );
  return metricId.replace(fbWindowsRegex, "");
};
const cleanFbMetricName = (metricName: string) =>
  metricName.replace(/ *\([^)]*\) */g, "");

const metricsWithoutAttributionWindow = <T extends MetricConfig>(
  metrics: T[],
) => {
  const cleanedMetrics: T[] = [];
  metrics.forEach((metric) => {
    const cleanedId = cleanFbMetricId(metric.id);
    const cleanedName = cleanFbMetricName(metric.name);
    const cleanedMetric = {
      ...metric,
      id: cleanedId,
      name: cleanedName,
    };

    const hasMetric = cleanedMetrics.some(
      ({ id, name }) => id === cleanedId && name === cleanedName,
    );
    if (!hasMetric) {
      cleanedMetrics.push(cleanedMetric);
    }
  });

  return orderBy(cleanedMetrics, "name");
};

const MetricsSelector = <T extends MetricConfig>({
  title,
  fbMetrics,
  toggleFbMetricVisibility,
  selectedFbMetrics,
  setSelectedFbMetrics,
  metricOrderedVisibility,
  selectedFbWindows,
  testId,
}: {
  title: string;
  fbMetrics: T[];
  toggleFbMetricVisibility: (arg0: T) => void;
  selectedFbMetrics: Set<string>;
  setSelectedFbMetrics: (arg0: Set<string>) => void;
  metricOrderedVisibility: T[];
  selectedFbWindows: Set<FB_WINDOW["value"]>;
  testId?: string;
}) => {
  const selectAll = () => {
    const newSelectedFbMetrics = new Set<string>([
      ...selectedFbMetrics,
      ...fbMetrics.map(({ id }) => id),
    ]);
    setSelectedFbMetrics(newSelectedFbMetrics);
  };

  const clearAll = () => {
    const newSelectedFbMetrics = new Set<string>([...selectedFbMetrics]);
    fbMetrics.forEach(({ id }) => {
      newSelectedFbMetrics.delete(id);
    });
    setSelectedFbMetrics(newSelectedFbMetrics);
  };

  const doesMetricWithAttributionWindowExist = (metric: T) => {
    return (
      !metric.isNonModal &&
      [...selectedFbWindows].every(
        (window: FB_WINDOW["value"]) =>
          !metricOrderedVisibility.find(
            ({ id }) => id === `${metric.id}${window}`,
          ),
      )
    );
  };

  // Disabled if combination of metric and attribution window does not exist
  const disabledMetrics = new Set(
    fbMetrics.filter(doesMetricWithAttributionWindowExist).map(({ id }) => id),
  );

  //
  const metricsPossibleAttributionWindows = useMemo(() => {
    const metricsWindows = new Map();
    metricOrderedVisibility.forEach((metric) => {
      const metricId = cleanFbMetricId(metric.id);

      const metricAttributionWindow = metric.id.replace(metricId, "");
      if (!metricsWindows.has(metricId)) {
        metricsWindows.set(metricId, new Set());
      }
      FB_WINDOWS_NAMES[metricAttributionWindow]?.name &&
        metricsWindows
          .get(metricId)
          .add(FB_WINDOWS_NAMES[metricAttributionWindow].name);
    });

    return metricsWindows;
  }, [metricOrderedVisibility]);

  return (
    <Accordion title={title} isExpandedByDefault testId={testId}>
      {fbMetrics.length > 0 ? (
        <>
          <div className="text-xxs mb-4 my-2">
            <button className="mr-4 hover:text-primary" onClick={selectAll}>
              Select All
            </button>
            <button className="hover:text-primary" onClick={clearAll}>
              Clear All
            </button>
          </div>
          <div className="grid grid-cols-2 text-xs font-light gap-0.5">
            {fbMetrics.map((metric) => (
              <MetricCheckbox
                key={metric.name}
                metric={metric}
                isDisabled={disabledMetrics.has(metric.id)}
                isChecked={selectedFbMetrics.has(metric.id)}
                toggleFbMetricVisibility={toggleFbMetricVisibility}
                metricAttributionWindows={[
                  ...metricsPossibleAttributionWindows.get(metric.id),
                ]}
              />
            ))}
          </div>
        </>
      ) : (
        <div className="mb-4">
          No metrics available for selected attribution window
        </div>
      )}
    </Accordion>
  );
};

const MetricCheckbox = <T extends MetricConfig>({
  metric,
  isDisabled,
  isChecked,
  toggleFbMetricVisibility,
  metricAttributionWindows,
}: {
  metric: T;
  isDisabled: boolean;
  isChecked: boolean;
  toggleFbMetricVisibility: (arg0: T) => void;
  metricAttributionWindows: string[];
}) => (
  <div
    key={metric.name}
    className="flex-horizontal-center cursor-pointer mb-4"
    onClick={() => !isDisabled && toggleFbMetricVisibility(metric)}
  >
    <Checkbox
      className="mr-3"
      isDisabled={isDisabled}
      onChange={() => {
        // Do nothing
      }}
      value={isChecked}
    />
    <span>{metric.name}</span>
    {isDisabled && (
      <GenericTooltip
        content={
          <span className="whitespace-pre-line">
            {`Combination of attribution window and metric does not exist. To select
              this metric you must select one of the following attribution windows:`}
            <br />
            <b>{metricAttributionWindows.join("\n")}</b>
          </span>
        }
      />
    )}
  </div>
);
